import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useQuery, useMutation, useQueryClient} from 'react-query'
import {Button, Form, Spinner} from 'react-bootstrap'
import {Formik, Form as FormikForm} from 'formik'
import * as Yup from 'yup'
import {toast} from 'react-toastify'
import {KTCard, KTCardBody} from '../../../_dashboard/helpers'
import {
  fetchExternalReviewConfigs,
  updateExternalReviewConfig,
  triggerExternalSync,
  fetchToursForSelect,
  ExternalReviewConfigRow,
} from './reviewsManagementApi'

const CONFIG_QK = ['external-review-configs']

const SYNC_OPTIONS = [6, 12, 24, 48] as const

function formatSync(dt?: string) {
  if (!dt) return '—'
  try {
    return new Date(dt).toLocaleString()
  } catch {
    return dt
  }
}

const ExternalReviewsConfigTab: React.FC = () => {
  const intl = useIntl()
  const qc = useQueryClient()

  const {data: configs = [], isLoading} = useQuery(CONFIG_QK, fetchExternalReviewConfigs)
  const {data: tours = []} = useQuery(['tours-for-external-config'], fetchToursForSelect, {
    staleTime: 60_000,
  })

  const google = useMemo(
    () => configs.find((c) => c.provider === 'google'),
    [configs]
  )
  const tripadvisor = useMemo(
    () => configs.find((c) => c.provider === 'tripadvisor'),
    [configs]
  )

  const saveMut = useMutation(
    ({provider, body}: {provider: 'google' | 'tripadvisor'; body: Record<string, unknown>}) =>
      updateExternalReviewConfig(provider, body),
    {
      onSuccess: () => {
        void toast.success(intl.formatMessage({id: 'REVIEWS.CONFIG.SAVE_OK'}))
        void qc.invalidateQueries(CONFIG_QK)
      },
      onError: () => {
        void toast.error(intl.formatMessage({id: 'REVIEWS.CONFIG.SAVE_ERR'}))
      },
    }
  )

  const syncMut = useMutation((provider: 'google' | 'tripadvisor') => triggerExternalSync(provider), {
    onSuccess: () => {
      void toast.info(intl.formatMessage({id: 'REVIEWS.CONFIG.SYNC_STARTED'}))
      void qc.invalidateQueries(CONFIG_QK)
    },
    onError: () => {
      void toast.error(intl.formatMessage({id: 'REVIEWS.CONFIG.SYNC_ERR'}))
    },
  })

  const googleSchema = Yup.object({
    placeId: Yup.string(),
    apiKey: Yup.string(),
    linkedTourId: Yup.string(),
  })

  const taSchema = Yup.object({
    locationId: Yup.string(),
    apiKey: Yup.string(),
    tripAdvisorLogoUrl: Yup.string(),
    linkedTourId: Yup.string(),
  })

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center py-10'>
        <Spinner animation='border' />
      </div>
    )
  }

  return (
    <div className='row g-6'>
      <div className='col-lg-6'>
        <KTCard>
          <KTCardBody>
            <h3 className='mb-1'>{intl.formatMessage({id: 'REVIEWS.CONFIG.GOOGLE_TITLE'})}</h3>
            <p className='text-muted fs-7 mb-5'>
              {intl.formatMessage({id: 'REVIEWS.CONFIG.GOOGLE_HINT'})}
            </p>

            <Formik
              enableReinitialize
              initialValues={{
                isEnabled: google?.isEnabled ?? false,
                apiKey: '',
                placeId: google?.placeId ?? '',
                syncIntervalHours: SYNC_OPTIONS.includes(
                  (google?.syncIntervalHours ?? 24) as (typeof SYNC_OPTIONS)[number]
                )
                  ? (google?.syncIntervalHours as number)
                  : 24,
                linkedTourId: (google?.linkedTourId as string) ?? '',
              }}
              validationSchema={googleSchema}
              onSubmit={(values) => {
                const body: Record<string, unknown> = {
                  isEnabled: values.isEnabled,
                  placeId: values.placeId.trim(),
                  syncIntervalHours: values.syncIntervalHours,
                }
                if (values.apiKey.trim()) body.apiKey = values.apiKey.trim()
                body.linkedTourId = values.linkedTourId || ''
                saveMut.mutate({provider: 'google', body})
              }}
            >
              {({values, handleChange, handleSubmit, setFieldValue}) => (
                <FormikForm onSubmit={handleSubmit}>
                  <Form.Check
                    type='switch'
                    id='google-enabled'
                    name='isEnabled'
                    checked={values.isEnabled}
                    onChange={handleChange}
                    label={intl.formatMessage({id: 'REVIEWS.CONFIG.ENABLE_SYNC'})}
                    className='mb-4 form-check-custom form-check-solid'
                  />

                  <Form.Group className='mb-3'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.API_KEY'})}</Form.Label>
                    <GoogleApiKeyField
                      value={values.apiKey}
                      onChange={(v) => setFieldValue('apiKey', v)}
                      maskedHint={google?.apiKeyMasked}
                    />
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.PLACE_ID'})}</Form.Label>
                    <Form.Control name='placeId' value={values.placeId} onChange={handleChange} />
                    <a
                      href='https://developers.google.com/maps/documentation/places/web-service/place-id'
                      target='_blank'
                      rel='noreferrer'
                      className='fs-8'
                    >
                      {intl.formatMessage({id: 'REVIEWS.CONFIG.PLACE_ID_HELP'})}
                    </a>
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.LINKED_TOUR'})}</Form.Label>
                    <Form.Select
                      name='linkedTourId'
                      value={values.linkedTourId}
                      onChange={handleChange}
                    >
                      <option value=''>{intl.formatMessage({id: 'REVIEWS.FILTER.ALL'})}</option>
                      {tours.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className='mb-4'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.SYNC_INTERVAL'})}</Form.Label>
                    <Form.Select
                      name='syncIntervalHours'
                      value={values.syncIntervalHours}
                      onChange={handleChange}
                    >
                      {SYNC_OPTIONS.map((h) => (
                        <option key={h} value={h}>
                          {h} h
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <SyncStatusBlock row={google} />

                  <div className='d-flex flex-wrap gap-2'>
                    <Button type='submit' variant='primary' disabled={saveMut.isLoading}>
                      {intl.formatMessage({id: 'REVIEWS.CONFIG.SAVE'})}
                    </Button>
                    <Button
                      type='button'
                      variant='light-primary'
                      disabled={syncMut.isLoading}
                      onClick={() => syncMut.mutate('google')}
                    >
                      {intl.formatMessage({id: 'REVIEWS.CONFIG.SYNC_NOW'})}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </KTCardBody>
        </KTCard>
      </div>

      <div className='col-lg-6'>
        <KTCard>
          <KTCardBody>
            <h3 className='mb-1'>{intl.formatMessage({id: 'REVIEWS.CONFIG.TA_TITLE'})}</h3>
            <p className='text-muted fs-7 mb-3'>
              {intl.formatMessage({id: 'REVIEWS.CONFIG.TA_LOGO_NOTE'})}
            </p>

            <Formik
              enableReinitialize
              initialValues={{
                isEnabled: tripadvisor?.isEnabled ?? false,
                apiKey: '',
                locationId: tripadvisor?.locationId ?? '',
                syncIntervalHours: SYNC_OPTIONS.includes(
                  (tripadvisor?.syncIntervalHours ?? 24) as (typeof SYNC_OPTIONS)[number]
                )
                  ? (tripadvisor?.syncIntervalHours as number)
                  : 24,
                linkedTourId: (tripadvisor?.linkedTourId as string) ?? '',
                tripAdvisorLogoUrl: tripadvisor?.tripAdvisorLogoUrl ?? '',
              }}
              validationSchema={taSchema}
              onSubmit={(values) => {
                const body: Record<string, unknown> = {
                  isEnabled: values.isEnabled,
                  locationId: values.locationId.trim(),
                  syncIntervalHours: values.syncIntervalHours,
                  tripAdvisorLogoUrl: values.tripAdvisorLogoUrl.trim(),
                }
                if (values.apiKey.trim()) body.apiKey = values.apiKey.trim()
                body.linkedTourId = values.linkedTourId || ''
                saveMut.mutate({provider: 'tripadvisor', body})
              }}
            >
              {({values, handleChange, handleSubmit, setFieldValue}) => (
                <FormikForm onSubmit={handleSubmit}>
                  <Form.Check
                    type='switch'
                    id='ta-enabled'
                    name='isEnabled'
                    checked={values.isEnabled}
                    onChange={handleChange}
                    label={intl.formatMessage({id: 'REVIEWS.CONFIG.ENABLE_SYNC'})}
                    className='mb-4 form-check-custom form-check-solid'
                  />

                  <Form.Group className='mb-3'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.API_KEY'})}</Form.Label>
                    <GoogleApiKeyField
                      value={values.apiKey}
                      onChange={(v) => setFieldValue('apiKey', v)}
                      maskedHint={tripadvisor?.apiKeyMasked}
                    />
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.LOCATION_ID'})}</Form.Label>
                    <Form.Control name='locationId' value={values.locationId} onChange={handleChange} />
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.TA_LOGO_URL'})}</Form.Label>
                    <Form.Control
                      name='tripAdvisorLogoUrl'
                      value={values.tripAdvisorLogoUrl}
                      onChange={handleChange}
                    />
                    {values.tripAdvisorLogoUrl && (
                      <div className='mt-2'>
                        <img
                          src={values.tripAdvisorLogoUrl}
                          alt='TripAdvisor'
                          style={{maxHeight: 40}}
                        />
                      </div>
                    )}
                  </Form.Group>

                  <Form.Group className='mb-3'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.LINKED_TOUR'})}</Form.Label>
                    <Form.Select
                      name='linkedTourId'
                      value={values.linkedTourId}
                      onChange={handleChange}
                    >
                      <option value=''>{intl.formatMessage({id: 'REVIEWS.FILTER.ALL'})}</option>
                      {tours.map((t) => (
                        <option key={t._id} value={t._id}>
                          {t.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className='mb-4'>
                    <Form.Label>{intl.formatMessage({id: 'REVIEWS.CONFIG.SYNC_INTERVAL'})}</Form.Label>
                    <Form.Select
                      name='syncIntervalHours'
                      value={values.syncIntervalHours}
                      onChange={handleChange}
                    >
                      {SYNC_OPTIONS.map((h) => (
                        <option key={h} value={h}>
                          {h} h
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <SyncStatusBlock row={tripadvisor} />

                  <div className='d-flex flex-wrap gap-2'>
                    <Button type='submit' variant='primary' disabled={saveMut.isLoading}>
                      {intl.formatMessage({id: 'REVIEWS.CONFIG.SAVE'})}
                    </Button>
                    <Button
                      type='button'
                      variant='light-primary'
                      disabled={syncMut.isLoading}
                      onClick={() => syncMut.mutate('tripadvisor')}
                    >
                      {intl.formatMessage({id: 'REVIEWS.CONFIG.SYNC_NOW'})}
                    </Button>
                  </div>
                </FormikForm>
              )}
            </Formik>
          </KTCardBody>
        </KTCard>
      </div>
    </div>
  )
}

const SyncStatusBlock: React.FC<{row?: ExternalReviewConfigRow}> = ({row}) => {
  const intl = useIntl()
  if (!row) return null
  return (
    <div className='bg-light rounded p-3 mb-4 fs-7'>
      <div>
        <strong>{intl.formatMessage({id: 'REVIEWS.CONFIG.LAST_SYNC'})}:</strong>{' '}
        {formatSync(row.lastSyncAt)}
      </div>
      <div>
        <strong>{intl.formatMessage({id: 'REVIEWS.CONFIG.LAST_STATUS'})}:</strong>{' '}
        {row.lastSyncStatus ?? '—'}
      </div>
      {row.lastSyncError ? (
        <div className='text-danger mt-1'>
          <strong>{intl.formatMessage({id: 'REVIEWS.CONFIG.LAST_ERROR'})}:</strong> {row.lastSyncError}
        </div>
      ) : null}
    </div>
  )
}

const GoogleApiKeyField: React.FC<{
  value: string
  onChange: (v: string) => void
  maskedHint?: string
}> = ({value, onChange, maskedHint}) => {
  const [show, setShow] = React.useState(false)
  const intl = useIntl()
  return (
    <>
      <InputGroupLike>
        <Form.Control
          className='flex-grow-1'
          type={show ? 'text' : 'password'}
          autoComplete='off'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={
            maskedHint
              ? intl.formatMessage({id: 'REVIEWS.CONFIG.KEY_PLACEHOLDER_MASKED'})
              : undefined
          }
        />
        <Button type='button' variant='light' size='sm' onClick={() => setShow((s) => !s)}>
          {show
            ? intl.formatMessage({id: 'REVIEWS.CONFIG.HIDE_KEY'})
            : intl.formatMessage({id: 'REVIEWS.CONFIG.SHOW_KEY'})}
        </Button>
      </InputGroupLike>
      {maskedHint ? (
        <Form.Text muted>
          {intl.formatMessage({id: 'REVIEWS.CONFIG.CURRENT_KEY'})}: {maskedHint}
        </Form.Text>
      ) : null}
    </>
  )
}

/** Simple flex row mimicking input-group without importing InputGroup quirks */
const InputGroupLike: React.FC<{children: React.ReactNode}> = ({children}) => (
  <div className='d-flex gap-2 align-items-stretch'>{children}</div>
)

export {ExternalReviewsConfigTab}
